import { createStore } from 'zustand/vanilla';

/** Page-lifetime reading context only. No storage, market objects or actions. */
export interface WorkingViewDefinition<T> {
  readonly id: string;
  /** Return a new allowlisted value, or null. Never spread an unknown input. */
  readonly parse: (input: unknown) => T | null;
}
export interface WorkingViewAddress {
  scope: string;
  epoch: number;
  view: string;
  instance: string;
  entry: string;
}
interface RecordValue { value: unknown; revision: number }
interface WorkingState {
  scope: string | null;
  epoch: number;
  revision: number;
  records: ReadonlyMap<string, RecordValue>;
  latest: ReadonlyMap<string, string>;
}
export const MAX_WORKING_VIEWS = 256;
export const workingViewStore = createStore<WorkingState>(() => ({
  scope: null, epoch: 0, revision: 0, records: new Map(), latest: new Map(),
}));
export const workingViewGroupKey = (a: WorkingViewAddress) => JSON.stringify([a.view, a.instance]);
export const workingViewRecordKey = (a: WorkingViewAddress) => JSON.stringify([a.view, a.instance, a.entry]);
const groupKey = workingViewGroupKey;
const recordKey = workingViewRecordKey;
export function workingAddressIsCurrent(a: WorkingViewAddress): boolean {
  const s = workingViewStore.getState();
  return s.scope === a.scope && s.epoch === a.epoch;
}
export function setWorkingViewScope(scope: string | null): void {
  const s = workingViewStore.getState();
  if (s.scope === scope) return;
  workingViewStore.setState({ scope, epoch: s.epoch + 1, records: new Map(), latest: new Map() });
}
export function resetWorkingViews(): void {
  const s = workingViewStore.getState();
  workingViewStore.setState({ epoch: s.epoch + 1, records: new Map(), latest: new Map() });
}
export function readWorkingView<T>(
  definition: WorkingViewDefinition<T>, address: WorkingViewAddress, resumeLatest: boolean,
): T | null {
  if (!workingAddressIsCurrent(address) || definition.id !== address.view) return null;
  const s = workingViewStore.getState();
  const exact = s.records.get(recordKey(address));
  const lastKey = resumeLatest ? s.latest.get(groupKey(address)) : undefined;
  const record = exact ?? (lastKey ? s.records.get(lastKey) : undefined);
  return record ? definition.parse(record.value) : null;
}
export function workingViewRevision(address: WorkingViewAddress): number | undefined {
  return workingAddressIsCurrent(address)
    ? workingViewStore.getState().records.get(recordKey(address))?.revision : undefined;
}
export function writeWorkingView<T>(
  definition: WorkingViewDefinition<T>, address: WorkingViewAddress, input: T,
): number | undefined {
  if (!workingAddressIsCurrent(address) || definition.id !== address.view) return undefined;
  const value = definition.parse(input);
  if (value === null) return undefined;
  const s = workingViewStore.getState();
  const records = new Map(s.records);
  const latest = new Map(s.latest);
  const key = recordKey(address);
  records.delete(key);
  records.set(key, { value, revision: s.revision + 1 });
  latest.set(groupKey(address), key);
  while (records.size > MAX_WORKING_VIEWS) records.delete(records.keys().next().value!);
  for (const [group, target] of latest) if (!records.has(target)) latest.delete(group);
  workingViewStore.setState({ records, latest, revision: s.revision + 1 });
  return s.revision + 1;
}

/** Same user/org but a revoked grant must not revive a now-inaccessible view. */
export function workingViewAuthScope(user: {
  id: string; orgId: string | null; role: string;
  lt2Access?: boolean; oneClickAccess?: boolean; attackAccess?: boolean;
  oscarAccess?: boolean; botAccess?: boolean;
} | null): string | null {
  return user ? JSON.stringify([user.id, user.orgId, user.role,
    !!user.lt2Access, !!user.oneClickAccess, !!user.attackAccess,
    !!user.oscarAccess, !!user.botAccess]) : null;
}
